import React, { useEffect, useRef, useState } from "react";
import { toast, Toaster } from "sonner";
import { Upload, RotateCcw, RotateCw, Image as ImageIcon } from "lucide-react";
import api from "../../lib/api";
import { useImages } from "../../lib/images";

const LABELS = {
  hero: "Hero — main image",
  heroSecondary: "Hero — secondary",
  drawing: "Module · Drawing",
  oilPastel: "Module · Oil Pastels",
  watercolor: "Module · Watercolours",
  acrylic: "Module · Acrylics",
  tutor: "About / Tutor",
  studioA: "Marquee · Studio A",
  studioB: "Marquee · Studio B",
  logo: "Studio logo (appears on vouchers + emails)",
};

const DESCRIPTIONS = {
  hero: "Large portrait image on the top-right of the home page.",
  heroSecondary: "Secondary/backup hero image used in marquee.",
  drawing: "Card + class page image for the Drawing module.",
  oilPastel: "Card + class page image for Oil Pastels.",
  watercolor: "Card + class page image for Watercolours.",
  acrylic: "Card + class page image for Acrylics.",
  tutor: "About page portrait and home CTA image.",
  studioA: "Extra image in the rolling photo marquee.",
  studioB: "Extra image in the rolling photo marquee.",
  logo: "Square or wide PNG/JPG with transparent background works best. Appears centred at the top of every gift voucher and email branding.",
};

export default function SiteImages() {
  const [data, setData] = useState({ keys: [], overrides: {} });
  const { images, reload } = useImages();
  const [busy, setBusy] = useState("");

  const load = async () => {
    const { data } = await api.get("/admin/site-images");
    setData(data);
  };
  useEffect(() => { load(); }, []);

  const upload = async (key, file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Only JPG, PNG or WebP accepted");
      return;
    }
    if (file.size > 12 * 1024 * 1024) {
      toast.error("Image too large (max 12 MB). Please resize.");
      return;
    }
    setBusy(key);
    try {
      const b64 = await toBase64(file);
      await api.put(`/admin/site-images/${key}`, {
        mime_type: file.type,
        filename: file.name,
        image_base64: b64.split(",")[1],
      });
      toast.success(`Updated "${LABELS[key]}"`);
      await load();
      await reload();
    } catch { toast.error("Upload failed"); } finally { setBusy(""); }
  };

  const reset = async (key) => {
    if (!confirm(`Revert "${LABELS[key]}" to the default image?`)) return;
    await api.delete(`/admin/site-images/${key}`);
    toast.success("Reverted to default");
    await load();
    await reload();
  };

  const rotate = async (key, degrees) => {
    try {
      await api.post(`/admin/site-images/${key}/rotate`, { degrees });
      await load();
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Can't rotate — upload a custom image first.");
    }
  };

  return (
    <div data-testid="admin-images-page">
      <Toaster position="top-center" />
      <div className="mb-7">
        <div className="font-hand text-[#A685BD] text-2xl">media library</div>
        <h1 className="font-display text-4xl text-[#5D2C8F]">Site images</h1>
        <p className="text-sm text-[#6E6074] mt-1">
          Replace any image on the public website — hero, class modules, tutor, marquee. Changes appear instantly.
        </p>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {(data.keys || []).map((key) => {
          const override = data.overrides?.[key];
          const src = images[key]; // merged override|default
          return (
            <Slot
              key={key}
              k={key}
              label={LABELS[key] || key}
              desc={DESCRIPTIONS[key] || ""}
              src={src}
              isOverride={!!override}
              busy={busy === key}
              onUpload={(f) => upload(key, f)}
              onReset={() => reset(key)}
              onRotate={(deg) => rotate(key, deg)}
            />
          );
        })}
      </div>
    </div>
  );
}

function Slot({ k, label, desc, src, isOverride, busy, onUpload, onReset, onRotate }) {
  const ref = useRef();
  return (
    <div className="admin-surface p-4 flex flex-col gap-3" data-testid={`image-slot-${k}`}>
      <div className="flex items-start gap-2">
        <ImageIcon size={16} className="text-[#A685BD] mt-0.5" />
        <div className="flex-1">
          <div className="font-display text-lg text-[#5D2C8F] leading-tight">{label}</div>
          <div className="text-xs text-[#6E6074]">{desc}</div>
        </div>
        {isOverride && <span className="text-[10px] uppercase tracking-widest text-[#8EC9C7] border border-[#8EC9C7]/50 rounded-full px-2 py-0.5">custom</span>}
      </div>

      <div className="aspect-[4/3] rounded-xl overflow-hidden bg-[#F4EDDD] relative group">
        {src ? <img src={src} alt={label} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-[#6E6074] text-sm">No image</div>}
        {isOverride && (
          <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button onClick={() => onRotate(270)} className="bg-white/90 hover:bg-white rounded-full p-1.5 shadow" title="Rotate left" data-testid={`image-rotate-left-${k}`}>
              <RotateCcw size={14} className="text-[#5D2C8F]" />
            </button>
            <button onClick={() => onRotate(90)} className="bg-white/90 hover:bg-white rounded-full p-1.5 shadow" title="Rotate right" data-testid={`image-rotate-right-${k}`}>
              <RotateCw size={14} className="text-[#5D2C8F]" />
            </button>
          </div>
        )}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <input
          ref={ref}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={(e) => onUpload(e.target.files?.[0])}
          data-testid={`image-input-${k}`}
        />
        <button onClick={() => ref.current?.click()} disabled={busy} className="sac-btn-primary !py-2 !px-4 text-sm" data-testid={`image-upload-btn-${k}`}>
          <Upload size={14} /> {busy ? "Uploading…" : isOverride ? "Replace" : "Upload"}
        </button>
        {isOverride && (
          <>
            <button onClick={() => onRotate(90)} className="text-xs text-[#5D2C8F] inline-flex items-center gap-1 px-2 py-1 border border-[#E5E0D8] rounded-full hover:bg-[#FAF6EE]" data-testid={`image-rotate-btn-${k}`}>
              <RotateCw size={12} /> Rotate
            </button>
            <button onClick={onReset} className="text-xs text-[#A685BD] inline-flex items-center gap-1 ml-auto" data-testid={`image-reset-btn-${k}`}>
              <RotateCcw size={12} /> Revert
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function toBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result); r.onerror = rej;
    r.readAsDataURL(file);
  });
}
